/**
 * LiveSummaryPanel
 * 
 * Displays an AI-generated real-time summary of the ongoing telehealth call.
 * Updates periodically as the conversation progresses.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Brain, RefreshCw, Clock } from 'lucide-react';
import transcriptService, { TranscriptLine } from '@/services/transcriptService';

interface LiveSummaryPanelProps {
  clinicId: string;
  patientName?: string;
  transcriptLines: TranscriptLine[];
  isTranscribing: boolean;
}

const LiveSummaryPanel: React.FC<LiveSummaryPanelProps> = ({
  clinicId,
  patientName,
  transcriptLines,
  isTranscribing
}) => {
  const [summary, setSummary] = useState<string>('');
  const [wordCount, setWordCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Track the number of lines we've summarized
  const lastSummarizedLineCount = useRef(0);
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Build transcript text from lines
  const buildTranscriptText = useCallback(() => {
    return transcriptLines
      .filter(line => line.isFinal !== false)
      .map(line => `${line.speakerName || 'Speaker'}: ${line.text}`)
      .join('\n');
  }, [transcriptLines]);

  // Generate summary
  const generateSummary = useCallback(async () => {
    const transcriptText = buildTranscriptText();
    
    // Don't summarize if too short
    if (transcriptText.length < 50) {
      setSummary('Waiting for more conversation...');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await transcriptService.generateLiveSummary(
        clinicId,
        transcriptText,
        patientName
      );

      if (response.success) {
        setSummary(response.summary);
        setWordCount(response.word_count);
        setLastUpdated(new Date());
        lastSummarizedLineCount.current = transcriptLines.length;
      }
    } catch (err: any) {
      console.error('[LiveSummaryPanel] Error generating summary:', err);
      const errorMessage = err?.response?.data?.message 
        || err?.response?.data?.error 
        || err?.message 
        || 'Failed to generate summary';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [clinicId, patientName, transcriptLines, buildTranscriptText]);

  // Auto-update summary every 30 seconds when transcribing
  useEffect(() => {
    if (isTranscribing && transcriptLines.length > 0) {
      // Initial summary after first few lines
      if (transcriptLines.length >= 3 && !summary) {
        generateSummary();
      }

      // Set up interval for periodic updates
      updateIntervalRef.current = setInterval(() => {
        // Only update if we have significantly more content (5+ new lines)
        if (transcriptLines.length - lastSummarizedLineCount.current >= 5) {
          generateSummary();
        }
      }, 30000); // Every 30 seconds

      return () => {
        if (updateIntervalRef.current) {
          clearInterval(updateIntervalRef.current);
        }
      };
    }
  }, [isTranscribing, transcriptLines.length, summary, generateSummary]);

  // Format "time ago"
  const formatTimeAgo = (date: Date) => {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
    if (seconds < 60) return 'just now';
    if (seconds < 120) return '1 min ago';
    return `${Math.floor(seconds / 60)} mins ago`;
  };

  return (
    <div className="flex flex-col bg-transparent h-full">
      {/* Compact Header - Refresh only */}
      <div className="flex items-center justify-end px-3 py-2 border-b border-slate-700/50">
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs text-slate-400 hover:text-slate-200 hover:bg-slate-700"
          onClick={generateSummary}
          disabled={isLoading || transcriptLines.length < 3}
        >
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
          ) : (
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
          )}
          Refresh
        </Button>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 p-3">
        {transcriptLines.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-500 py-8">
            <Brain className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm text-center">
              AI summary will appear here as the conversation progresses
            </p>
          </div>
        ) : isLoading && !summary ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 py-8">
            <Loader2 className="h-8 w-8 mb-2 animate-spin text-purple-400" />
            <p className="text-sm">Analyzing conversation...</p>
          </div>
        ) : error ? (
          <div className="text-red-400 text-sm p-2 bg-red-500/10 rounded-md">
            {error}
          </div>
        ) : summary ? (
          <div className="space-y-3">
            <div 
              className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ 
                __html: summary
                  .replace(/^[•\-]\s*/gm, '<span class="text-purple-400 mr-2">•</span>')
                  .replace(/\n/g, '<br/>')
              }}
            />
          </div>
        ) : (
          <p className="text-sm text-slate-500 italic">
            Waiting for more conversation...
          </p>
        )}
      </ScrollArea>

      {/* Footer with stats */}
      {summary && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-700/50 text-xs text-slate-500">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {lastUpdated ? formatTimeAgo(lastUpdated) : 'Not updated'}
          </div>
          <div>{wordCount} words analyzed</div>
        </div>
      )}
    </div>
  );
};

export default LiveSummaryPanel;













